/* @HEADER
 * @version 1.0.1819 | 2026-09-21
 * @purpose The top-level render-crash boundary: a throwing subtree becomes a crash screen with the error text, «Reload» and «Clear data» — worded from the stored locale, since this boundary wraps the MessagesProvider (ZVX-DEV-6381).
 * @sidecar ErrorBoundary.tsx.header.md
 * @layer app | @hex _none_ | @ctx _none_
 * @public false
 * @edit careful
 */

import { Component } from 'react'
import type { ErrorInfo, ReactNode } from 'react'
import { resolveMessages, readStoredLocale } from '../shared/i18n/resolveLocaleMessages'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

/**
 * Top-level error boundary that catches render crashes and shows a
 * recovery UI instead of a blank white screen. Users can click "Reload"
 * to refresh, or "Clear data" to wipe IndexedDB in case the stored data
 * itself is causing the crash.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack)
  }

  handleReload = () => {
    window.location.reload()
  }

  handleClearData = () => {
    const dbs = ['zvenix', 'zvenix-filter-views']
    for (const name of dbs) {
      indexedDB.deleteDatabase(name)
    }
    localStorage.clear()
    window.location.reload()
  }

  render() {
    if (this.state.hasError) {
      // ZVX-DEV-6381 -- this boundary wraps the MessagesProvider, so there is no
      // context to read; the stored locale is resolved directly. A crash
      // screen in the person's own language, not the one the bundle was
      // written in.
      const m = resolveMessages(readStoredLocale()).errorBoundary
      return (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', height: '100vh', gap: '16px',
          fontFamily: 'system-ui, sans-serif', color: '#334155',
        }}>
          <h2 style={{ margin: 0 }}>{m.title}</h2>
          <p style={{ margin: 0, color: '#64748b', maxWidth: '500px', textAlign: 'center' }}>
            {m.body}
          </p>
          <pre style={{
            background: '#f1f5f9', padding: '12px 16px', borderRadius: '8px',
            fontSize: '12px', maxWidth: '600px', overflow: 'auto', color: '#ef4444',
          }}>
            {this.state.error?.message}
          </pre>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={this.handleReload}
              style={{
                padding: '8px 20px', background: '#3b82f6', color: '#fff',
                border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px',
              }}
            >
              {m.reload}
            </button>
            <button
              onClick={this.handleClearData}
              style={{
                padding: '8px 20px', background: '#ef4444', color: '#fff',
                border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px',
              }}
            >
              {m.clearData}
            </button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}
